import type { CreateRunnerRequest, RunnerWatchdog, UpdateRunnerRequest } from '@/api/runners';
import { clockTime } from '@/lib/scaling';

export const WATCHDOG_MIN_TIMEOUT = 5;
export const WATCHDOG_MAX_TIMEOUT = 60;
export const WATCHDOG_DEFAULT_TIMEOUT = 10;
export const WATCHDOG_DAILY_RESTARTS = 3;

export const WATCHDOG_DESCRIPTION =
  'Restarts the runner (pause and resume, the IP stays) when it has not reported for this long. ' +
  `At most ${WATCHDOG_DAILY_RESTARTS} restarts a day.`;
export const WATCHDOG_LIMIT_NOTE =
  `Watchdog switched off after ${WATCHDOG_DAILY_RESTARTS} restarts within 24 hours. ` +
  'Enable it again in the runner settings.';
export const WATCHDOG_LIMIT_FORM_NOTE = `Switched off after ${WATCHDOG_DAILY_RESTARTS} restarts within 24 hours. Switch it on to watch the runner again.`;

export interface WatchdogForm {
  enabled: boolean;
  /** A number, or an empty string while the input is empty. */
  timeoutMinutes: number | string;
}

type WatchdogFields = Pick<
  CreateRunnerRequest & UpdateRunnerRequest,
  'watchdogEnabled' | 'watchdogTimeoutMinutes'
>;

/** The watchdog watches the collector, which only runs with Monitoring or Metrics. */
export function watchdogAvailable(
  monitoringEnabled: boolean,
  metricsConnectionId: string
): boolean {
  return monitoringEnabled || metricsConnectionId !== '';
}

export function validateWatchdogTimeout(value: unknown): string {
  const minutes = typeof value === 'number' ? value : Number.NaN;
  return Number.isInteger(minutes) &&
    minutes >= WATCHDOG_MIN_TIMEOUT &&
    minutes <= WATCHDOG_MAX_TIMEOUT
    ? ''
    : `Enter a whole number of minutes from ${WATCHDOG_MIN_TIMEOUT} to ${WATCHDOG_MAX_TIMEOUT}.`;
}

/** Create: the backend treats omitted fields as off. */
export function watchdogCreateFields(form: WatchdogForm, available: boolean): WatchdogFields {
  if (!available || !form.enabled) return {};
  return { watchdogEnabled: true, watchdogTimeoutMinutes: Number(form.timeoutMinutes) };
}

/**
 * Update: only changes are sent, because omitted fields keep the stored settings, so a watchdog that the platform
 * switched off while the form was open is not switched on again by an unrelated save.
 */
export function watchdogUpdateFields(form: WatchdogForm, original: WatchdogForm): WatchdogFields {
  const fields: WatchdogFields = {};
  if (form.enabled !== original.enabled) fields.watchdogEnabled = form.enabled;
  if (form.enabled && Number(form.timeoutMinutes) !== Number(original.timeoutMinutes)) {
    fields.watchdogTimeoutMinutes = Number(form.timeoutMinutes);
  }
  return fields;
}

export function watchdogFormOf(watchdog: RunnerWatchdog | null | undefined): WatchdogForm {
  return {
    enabled: watchdog?.enabled === true,
    timeoutMinutes: watchdog?.timeoutMinutes ?? WATCHDOG_DEFAULT_TIMEOUT,
  };
}

export interface WatchdogSummary {
  /** For example "On · after 10 min" or "Off". */
  state: string;
  /** For example "2 restarts · last 14:02"; empty before the first restart. */
  restarts: string;
  /** The full timestamp of the last restart, or empty. */
  restartsTitle: string;
  /** The last restart could not resume the runner. */
  resumeFailed: boolean;
  /** Set when the platform switched the watchdog off after too many restarts. */
  limitNote: string;
}

export function watchdogSummary(watchdog: RunnerWatchdog, now: number): WatchdogSummary {
  const count = watchdog.restarts ?? 0;
  const last = watchdog.lastRestartAt ? new Date(watchdog.lastRestartAt) : null;
  const lastValid = last && !Number.isNaN(last.getTime()) ? last : null;
  const resumeFailed = watchdog.lastRestartResult === 'RESUME_FAILED';
  const parts: string[] = [];
  if (count > 0 || lastValid) parts.push(`${count} ${count === 1 ? 'restart' : 'restarts'}`);
  if (lastValid) parts.push(`last ${clockTime(lastValid, new Date(now))}`);
  if (resumeFailed) parts.push('resume failed');
  return {
    state: watchdog.enabled ? `On · after ${watchdog.timeoutMinutes} min` : 'Off',
    restarts: parts.join(' · '),
    restartsTitle: lastValid ? `Last restart ${lastValid.toLocaleString()}` : '',
    resumeFailed,
    limitNote:
      !watchdog.enabled && watchdog.disabledReason === 'RESTART_LIMIT' ? WATCHDOG_LIMIT_NOTE : '',
  };
}
